import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const YsletaCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Ysleta - Bilingual | Curipod"
      subTitle="See how Curipod helped Ysleta Middle School ignite writing growth for emergent bilingual students with immediate AI feedback and increased engagement."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>Igniting Writing Growth</HeroTitle>
            <HeroSubtitle>An Ysleta Middle School Impact Story</HeroSubtitle>
            <HeroDescription>
              How veteran teachers with 64 years of combined experience partnered with
              Curipod to maximize every minute of instruction for emergent bilingual
              students — Ysleta ISD, less than two miles from the U.S.–Mexico border.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Ysleta ISD</MetaBadge>
              <MetaBadge>6th &amp; 8th Grade ELA</MetaBadge>
              <MetaBadge>Emergent Bilingual</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatIcon>⏱</StatIcon>
                  <StatHighlight>45 min</StatHighlight>
                  <StatLabel>
                    Daily instructional time for reading &amp; writing
                  </StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatIcon>👩‍🏫</StatIcon>
                  <StatHighlight>64 yrs</StatHighlight>
                  <StatLabel>Combined teaching experience</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatIcon>🌎</StatIcon>
                  <StatHighlight>&lt; 2 mi</StatHighlight>
                  <StatLabel>From the U.S.–Mexico border</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* A SCHOOL ON THE BORDER WITH BIG GOALS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>A SCHOOL ON THE BORDER WITH BIG GOALS</SectionTag>
              <SectionTitle>A Familiar Challenge</SectionTitle>
              <SectionText>
                Ysleta Middle School sits less than two miles from the U.S.–Mexico border,
                serving a student body where many are emergent bilinguals navigating
                language learning on a daily basis.
              </SectionText>
              <SectionText>
                For 6th-grade teacher <strong>Melissa Castillo</strong> and 8th-grade
                teacher <strong>Lori Kohan-Nanez</strong>, the challenge was clear:
                teaching both reading and writing to students with diverse language needs
                in just 45 minutes per day. Every minute had to count.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  We have a lot of emergent bilingual students. The border is less than
                  two miles from where I&apos;m sitting… using the language is a daily
                  struggle.
                </QuoteText>
                <QuoteAttrib>
                  — Elyse Hernandez, Instructional Technology Coach
                </QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamCardTitle>The Ysleta Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>6th Grade Teacher</TeamRole>
                  <TeamName>Melissa Castillo</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>8th Grade Teacher</TeamRole>
                  <TeamName>Lori Kohan-Nanez</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Instructional Technology Coach</TeamRole>
                  <TeamName>Elyse Hernandez</TeamName>
                  <TeamNote>Coaching support &amp; Curipod champion</TeamNote>
                </TeamMember>
              </TeamMemberList>
              <FocusAreaSection>
                <FocusAreaLabel>Focus Areas</FocusAreaLabel>
                <FocusAreaGrid>
                  {[
                    'Immediate Feedback',
                    'Confidence',
                    'Limited Time',
                    'Bilingual Support',
                  ].map(area => (
                    <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
                  ))}
                </FocusAreaGrid>
              </FocusAreaSection>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE CHALLENGE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Time Was Short, Feedback Came Too Late
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Time was short, engagement was inconsistent, and feedback often came too
              late to make an impact. Traditional feedback cycles stretched over days,
              leaving students disengaged. Many struggled to see themselves as capable
              writers.
            </SectionText>

            <BlockQuote style={{ maxWidth: 640, margin: '0 auto 40px' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                When you hold back feedback for more than 24 hours, it becomes less
                meaningful. Being able to give immediate feedback is a game-changer.
              </QuoteText>
              <QuoteAttrib>— Elyse Hernandez, Instructional Technology Coach</QuoteAttrib>
            </BlockQuote>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 0' }}
            >
              Teachers knew what mattered — timely feedback, active participation, and
              visible growth — but without the right tools, delivering on those priorities
              felt impossible.
            </SectionText>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE CURIPOD SHIFT — 4 IMPACT AREAS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE CURIPOD SHIFT
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            How Curipod Transformed the Classroom
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>💬</ImpactIcon>
              <ImpactTitle>Immediate, Actionable Feedback</ImpactTitle>
              <ImpactQuote>
                &ldquo;Curipod transformed writing into something immediate, motivating,
                and measurable for my students.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Melissa Castillo</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🤝</ImpactIcon>
              <ImpactTitle>Engagement &amp; Community</ImpactTitle>
              <ImpactQuote>
                &ldquo;My classroom feels like a community. Students engaged, discussed,
                and held each other accountable.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Melissa Castillo</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Best-Practice Pedagogy</ImpactTitle>
              <ImpactQuote>
                &ldquo;Curipod has our students excited. It brings Kagan-style strategies
                back into the room.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Elyse Hernandez</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🔥</ImpactIcon>
              <ImpactTitle>Safe Participation</ImpactTitle>
              <ImpactQuote>
                &ldquo;It&apos;s electric! Like a fire rocket — you&apos;re igniting it,
                and then we watch the kids go off. It&apos;s beautiful to see.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Lori Kohan-Nanez</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>

          <ScreenshotRow>
            <ScreenshotItem>
              <ScreenshotWrap>
                <Image
                  src="/case-study-ysleta/curipod-student-responses.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="Student responses in Curipod"
                />
              </ScreenshotWrap>
              <ScreenshotCaption>
                Students respond to inference prompts in real time
              </ScreenshotCaption>
            </ScreenshotItem>
            <ScreenshotItem>
              <ScreenshotWrap>
                <Image
                  src="/case-study-ysleta/curipod-ai-feedback.png"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="AI feedback on student writing"
                />
              </ScreenshotWrap>
              <ScreenshotCaption>
                AI feedback helps students revise and improve on the spot
              </ScreenshotCaption>
            </ScreenshotItem>
          </ScreenshotRow>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              THE RESULTS
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Undeniable Outcomes
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              Though district-wide comparisons are complex, the outcomes were undeniable.
            </SectionText>

            <ResultsList>
              <ResultItem>
                In Castillo&apos;s class, the number of students who received a zero on
                their <strong>STAAR ECR was drastically reduced</strong>
              </ResultItem>
              <ResultItem>
                Castillo&apos;s consistent use of Curipod{' '}
                <strong>boosted student confidence</strong>, allowing them to take more
                risks in their writing, leading directly to improved results on their
                state test scores
              </ResultItem>
              <ResultItem>
                In Kohan-Nanez&apos;s classes, students&apos; consistent use of Curipod
                for practice and assessment on STAAR writing prompts allowed them to
                incorporate the feedback and improve their writing skills,{' '}
                <strong>
                  contributing to the growth and success of her classes&apos; scores
                </strong>
              </ResultItem>
            </ResultsList>

            <ResultNote>
              For students, the shift wasn&apos;t just about scores but also about
              confidence. One student who had never earned points on a STAAR writing task
              finally did, and the achievement carried him for weeks.
            </ResultNote>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                It doesn&apos;t put them on the spot because it changes their name. But it
                helped them give a little extra effort because they knew it was going to
                be on the screen.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Lori Kohan-Nanez, 8th Grade Teacher
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Together, these shifts — <strong>immediate feedback</strong>,{' '}
              <strong>visible growth</strong>, and <strong>safe participation</strong> —
              transformed how students approached writing and how teachers measured
              success.
            </ResultNote>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* FROM CHALLENGE TO CHANGE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              FROM CHALLENGE TO CHANGE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For Ysleta Middle School, Curipod provided what teachers had always wanted:
              a way to fit best practices into limited time, to give feedback that
              mattered, and to see growth in every student.
            </SectionText>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                Curipod is the most innovative tool I&apos;ve used in my classroom in the
                last five years!
              </FinalQuoteText>
              <QuoteAttrib>— Melissa Castillo, 6th Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              When strong teaching meets the right tools, students rise, no matter how
              limited the minutes or how high the hurdles.
            </SectionText>

            <TagRow>
              {['Feedback', 'Confidence', 'Engagement', 'Bilingual Support'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Ysleta Middle School case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Ysleta_Middle_School.pdf"
              download="Curipod_Case_Study_Ysleta_Middle_School.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default YsletaCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 10px 14px;
`;

const StatIcon = styled.div`
  font-size: 28px;
  margin-bottom: 6px;
`;

const StatHighlight = styled.div`
  font-size: 40px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;

  @media ${MOBILE} {
    font-size: 32px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
  max-width: 200px;
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid ${theme.colors.beigeDark};
`;

const FocusAreaLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: bold;
  padding: 6px 16px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── SCREENSHOTS ────────────────────────────────────────────────────────────── */

const ScreenshotRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 48px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ScreenshotItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ScreenshotWrap = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${theme.effects.defaultShadow};
  border: 2px solid ${theme.colors.beigeDark};
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px;
  max-width: 720px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultItem = styled.li`
  font-size: 17px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.7;
  padding-left: 28px;
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.orange};
    font-weight: bold;
    font-size: 18px;
  }

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 40px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
